/**
 * useNpcArchetypesLibrary — 2026-06-08 Phase 6 wire-up for the canonical
 * Core Rulebook §28 NPC archetype generator tables.
 *
 * Mirrors useQBranchPersonnelLibrary.ts pattern: 3-state union + cancelled
 * flag unmount safety + memoised service stabilisation + 3-tab state
 * (allies/enemies/neutrals).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ArchetypeManifestEntry } from '../services/host/NpcArchetypesServiceModule';
import { NpcArchetypesServiceModule } from '../services/host/NpcArchetypesServiceModule';

export type NpcArchetypesLibraryState =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error';

export type NpcArchetypesLibraryTab = 'allies' | 'enemies' | 'neutrals';

export interface NpcArchetypesLibraryHookResult {
  state: NpcArchetypesLibraryState;
  archetypes: ArchetypeManifestEntry[];
  error: string | null;
  refresh: () => void;
  activeTab: NpcArchetypesLibraryTab;
  setActiveTab: (tab: NpcArchetypesLibraryTab) => void;
  selectedArchetypeId: string | null;
  selectedArchetypeData: Record<string, unknown> | null;
  selectedArchetypeState: NpcArchetypesLibraryState;
  selectArchetype: (id: string | null) => void;
}

export function useNpcArchetypesLibrary(
  archetypesService?: NpcArchetypesServiceModule,
): NpcArchetypesLibraryHookResult {
  const resolvedService = useMemo(
    () => archetypesService ?? new NpcArchetypesServiceModule(),
    [archetypesService],
  );

  const [state, setState] = useState<NpcArchetypesLibraryState>('idle');
  const [archetypes, setArchetypes] = useState<ArchetypeManifestEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const [activeTab, setActiveTab] =
    useState<NpcArchetypesLibraryTab>('allies');

  const [selectedArchetypeId, setSelectedArchetypeId] = useState<string | null>(
    null,
  );
  const [selectedArchetypeData, setSelectedArchetypeData] = useState<
    Record<string, unknown> | null
  >(null);
  const [selectedArchetypeState, setSelectedArchetypeState] =
    useState<NpcArchetypesLibraryState>('idle');

  const refresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  const selectArchetype = useCallback((id: string | null) => {
    setSelectedArchetypeId(id);
    if (id === null) {
      setSelectedArchetypeData(null);
      setSelectedArchetypeState('idle');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    setState('loading');
    setError(null);

    (async () => {
      try {
        const result = await resolvedService.list();
        if (cancelled) return;
        setArchetypes(result.archetypes);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        setArchetypes([]);
        setError(e instanceof Error ? e.message : String(e));
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, refreshTick]);

  useEffect(() => {
    if (selectedArchetypeId === null) {
      return;
    }
    let cancelled = false;

    setSelectedArchetypeState('loading');
    setSelectedArchetypeData(null);

    (async () => {
      try {
        const result = await resolvedService.get(selectedArchetypeId);
        if (cancelled) return;
        if (result === null) {
          setSelectedArchetypeState('error');
          return;
        }
        setSelectedArchetypeData(result);
        setSelectedArchetypeState('ready');
      } catch {
        if (cancelled) return;
        setSelectedArchetypeState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, selectedArchetypeId]);

  return {
    state,
    archetypes,
    error,
    refresh,
    activeTab,
    setActiveTab,
    selectedArchetypeId,
    selectedArchetypeData,
    selectedArchetypeState,
    selectArchetype,
  };
}
